import type { League, LeagueSquad, Player } from '../types/database';

/**
 * Pure helpers backing the AdminCricketTransferPanel (admin-on-behalf transfer).
 *
 * Design constraint: the actual swap math, banking and validation are owned by
 * ./cricketTransfer.ts (`computeCricketTransferUpdate`) — the same function the
 * player-facing SquadSelectionPage uses. These helpers exist purely to
 * (a) drive the admin dropdowns and (b) reshape the league's player pool into
 * the `Player[]` shape the existing TransferModal expects. They MUST NOT
 * duplicate any transfer logic.
 *
 * Mirrors src/utils/adminTennisTransfer.ts, adapted for cricket's multi-type
 * transfer model (bench / flexible / mid-season / wildcard): remaining
 * allowance is collapsed into a single number for the dropdown label.
 */

export interface AdminCricketSquadOption {
  squad: LeagueSquad;
  /** Sum of remaining allowance across all enabled transfer types. */
  transfersLeft: number;
  /** True when no transfers remain across any type. */
  isDisabled: boolean;
  /** Dropdown-ready label: "{squadName} — {N} left". */
  displayName: string;
}

export function getCricketLeagueOptionsForAdminTransfer(leagues: League[]): League[] {
  return leagues
    .filter((l) => l && l.sport === 'cricket')
    .slice()
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

/**
 * Remaining allowance for a single squad, summed across every enabled transfer
 * type. Mirrors TransferModal.getAvailableTransfers but ignores the live
 * `*ChangesEnabled` toggles — this reflects the squad's *allowance*, not whether
 * the player-facing window happens to be open right now.
 */
function cricketTransfersRemaining(squad: LeagueSquad, league: League): number {
  const t = league.transferTypes;
  if (!t) return 0;

  const remaining = (
    enabled: boolean | undefined,
    maxAllowed: number | undefined,
    used: number | undefined,
  ): number => (enabled ? Math.max(0, (maxAllowed ?? 0) - (used ?? 0)) : 0);

  return (
    remaining(t.benchTransfers?.enabled, t.benchTransfers?.maxAllowed, squad.benchTransfersUsed) +
    remaining(t.flexibleTransfers?.enabled, t.flexibleTransfers?.maxAllowed, squad.flexibleTransfersUsed) +
    remaining(t.midSeasonTransfers?.enabled, t.midSeasonTransfers?.maxAllowed, squad.midSeasonTransfersUsed) +
    remaining(t.wildcardTransfers?.enabled, t.wildcardTransfers?.maxAllowed, squad.wildcardTransfersUsed)
  );
}

export function getCricketSquadOptionsForAdminTransfer(
  squads: LeagueSquad[],
  league: League,
): AdminCricketSquadOption[] {
  const options: AdminCricketSquadOption[] = squads.map((squad) => {
    const transfersLeft = cricketTransfersRemaining(squad, league);
    const isDisabled = transfersLeft <= 0;
    const displayName = `${squad.squadName} — ${transfersLeft} left`;
    return { squad, transfersLeft, isDisabled, displayName };
  });

  options.sort((a, b) =>
    (a.squad.squadName || '').localeCompare(b.squad.squadName || ''),
  );

  return options;
}

/**
 * Reshape player-pool entries into the `Player[]` shape the cricket TransferModal
 * and computeCricketTransferUpdate expect. Mirrors the mapping in
 * SquadSelectionPage's pool loader so both paths produce identical Player rows.
 */
export function mapCricketPoolToPlayers(
  poolPlayers: Array<{
    playerId: string;
    name: string;
    team: string;
    role: Player['role'];
    points: number;
    isOverseas?: boolean;
    battingInnings?: unknown;
    bowlingSpells?: unknown;
    fieldingPerformances?: unknown;
  }>,
): Player[] {
  return poolPlayers.map((entry) => {
    const blankFormat = {
      matches: 0,
      runs: 0,
      wickets: 0,
      economy: 0,
      strikeRate: 0,
      catches: 0,
      recentForm: entry.points,
    };
    return {
      id: entry.playerId,
      name: entry.name,
      team: entry.team,
      country: 'India',
      role: entry.role,
      isActive: true,
      availability: 'available' as const,
      isOverseas: entry.isOverseas ?? false,
      stats: {
        T20: { ...blankFormat },
        ODI: { ...blankFormat },
        Test: { ...blankFormat },
      },
      createdAt: new Date(),
      updatedAt: new Date(),
      // Pass-through performance arrays so the modal can show recent-match detail.
      ...(entry.battingInnings ? { battingInnings: entry.battingInnings } : {}),
      ...(entry.bowlingSpells ? { bowlingSpells: entry.bowlingSpells } : {}),
      ...(entry.fieldingPerformances ? { fieldingPerformances: entry.fieldingPerformances } : {}),
    } as Player;
  });
}
